import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, PlusCircle } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select } from '../../components/ui/select';
import { Pagination } from '../../components/ui/Pagination';
import { Skeleton } from '../../components/ui/skeleton';
import { InlineCsatRating } from '../../components/ui/InlineCsatRating';
import { useTickets } from '../../hooks/use-tickets';
import { STATUS_LABELS, STATUS_COLORS, PRIORITY_LABELS, PRIORITY_COLORS, ALL_STATUSES, ALL_PRIORITIES, getStatusLabel, formatTicketId } from '../../lib/constants';
import { formatDate } from '../../lib/utils';

export function TicketsPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');

  const { data, isLoading } = useTickets({ page, pageSize: 10, search: search || undefined, status: status || undefined, priority: priority || undefined });
  const tickets = data?.data ?? [];
  const pagination = data?.meta.pagination;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text">Tichetele mele</h1>
        <Link to="/tickets/new">
          <Button><PlusCircle size={18} /> Tichet Nou</Button>
        </Link>
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <Input placeholder="Caută..." className="pl-9" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
          </div>
          <Select
            options={[{ value: '', label: 'Toate statusurile' }, ...ALL_STATUSES.map((s) => ({ value: s, label: STATUS_LABELS[s] }))]}
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          />
          <Select
            options={[{ value: '', label: 'Toate prioritățile' }, ...ALL_PRIORITIES.map((p) => ({ value: p, label: PRIORITY_LABELS[p] }))]}
            value={priority}
            onChange={(e) => { setPriority(e.target.value); setPage(1); }}
          />
        </div>
      </Card>

      <Card>
        <div className="divide-y">
          {isLoading && Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 px-6 py-4">
              <Skeleton className="h-4 w-full" />
            </div>
          ))}
          {!isLoading && tickets.length === 0 && (
            <p className="px-6 py-8 text-center text-sm text-gray-400">Nu s-au găsit tichete</p>
          )}
          {tickets.map((ticket) => (
            <div key={ticket.id} className="flex items-center justify-between px-6 py-4 transition-colors hover:bg-gray-50 dark:hover:bg-dark-border/50">
              <Link to={`/tickets/${ticket.id}`} className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-gray-400 dark:text-dark-muted">#{formatTicketId(ticket.numericId)}</span>
                  <p className="truncate font-medium text-gray-900 dark:text-dark-text">{ticket.subject}</p>
                </div>
                <p className="text-xs text-gray-500 dark:text-dark-muted">Creat pe {formatDate(ticket.createdAt)}</p>
              </Link>
              <div className="flex items-center gap-2">
                {ticket.status === 'RESOLVED' && <InlineCsatRating ticketId={ticket.id} />}
                {ticket.status === 'WAITING_CLIENT' && <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300">La mine</Badge>}
                <Badge className={PRIORITY_COLORS[ticket.priority]}>{PRIORITY_LABELS[ticket.priority]}</Badge>
                <Link to={`/tickets/${ticket.id}`}>
                  <Badge className={STATUS_COLORS[ticket.status]}>{getStatusLabel(ticket.status, 'CLIENT')}</Badge>
                </Link>
              </div>
            </div>
          ))}
        </div>
        {pagination && <Pagination page={pagination.page} totalPages={pagination.totalPages} onPageChange={setPage} />}
      </Card>
    </div>
  );
}
